import React, { useState, useEffect, useContext, useMemo } from "react";
import { CreateButton, useSelect, useTable } from "@refinedev/antd";
import {
  Input,
  Table,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  Badge,
  Tooltip,
  Pagination,
} from "antd";
import {
  BaseRecord,
  CrudFilters,
  GetListResponse,
  HttpError,
  useNavigation,
} from "@refinedev/core";
import { SellersFilters } from "@/pages/sellers/sellersFilters";
import { TableRowSelection } from "antd/es/table/interface";
import { PlusOutlined, ShopOutlined } from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { PaginationTotal } from "@/components/pagination-total";
import { SellerIcon } from "@/components/icon";
import { useFormatLastUpdate } from "@/utilities/date";
import dayjs from "dayjs";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { FilterTags } from "@/components";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text } = Typography;
const { Search } = Input;

export const SellersList: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [totalFilterTag, setTotalFilterTag] = useState(0);
  const screens = Grid.useBreakpoint();
  const { setSmartMenuState, setSmartMenuName, setSmartMenuLabel, setSmartMenuType } =
    useContext(SmartMenuContext);
  const formatLastUpdate = useFormatLastUpdate();

  const { token } = theme.useToken();
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();
  const { push } = useNavigation();

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const {
      main_search,
      sellers,
      stores,
      state,
      calendar,
      filters: existingFilters,
    } = params;

    if (existingFilters) {
      existingFilters.forEach((filter: any) => {
        filters.push(filter);
      });
    }

    filters.push(
      {
        field: "search",
        operator: "contains",
        value: main_search,
      },
      {
        field: "name",
        operator: "contains",
        value: sellers,
      },
      {
        field: "linked_store",
        operator: "eq",
        value: stores,
      },
      {
        field: "state",
        operator: "eq",
        value: state,
      },
      {
        field: "last_update",
        operator: "between",
        value: calendar
          ? [dayjs(calendar[0])?.format(), dayjs(calendar[1]).format()]
          : undefined,
      },
    );

    setFilters(filters);
    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
  } = useTable<HttpError>({
    resource: "sellers",
    pagination: {
      pageSize: FILAS_POR_PAGINA,
      mode: "server",
    },
    syncWithLocation: true,
    onSearch: onSearch,
  });

  const { selectProps: sellersSelectProps } = useSelect({
    resource: "sellers",
    optionLabel: "name",
    optionValue: "name",
  });

  const { selectProps: storesSelectProps } = useSelect({
    resource: "sellers",
    optionLabel: (item) => item.name,
    optionValue: (item) => item.store_id,
    queryOptions: {
      select: (data) =>
      ({
        data: data.data.reduce(
          (acc, seller) => acc.concat(seller.linked_store),
          [],
        ),
      } as GetListResponse<BaseRecord>),
    },
  });

  // Checkbox
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    // Lo elegido en otras páginas sigue elegido (xcons-docs#499).
    preserveSelectedRowKeys: true,
    selections: [Table.SELECTION_NONE],
  };

  useEffect(() => {
    if (tableProps.pagination && tableProps.pagination.total) {
      setTotalFilterTag(tableProps.pagination.total);
    }
  }, [tableProps.pagination]);

  const isLoadingTable = !data || isFetching;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : data?.data || []),
    [isLoadingTable, data],
  );

  const columns: TableColumnsType<any> = [
    {
      key: "name",
      title: t("menu.label.sellers"),
      dataIndex: "name",
      sorter: (a, b) =>
        a.name.toLowerCase().charCodeAt(0) - b.name.toLowerCase().charCodeAt(0),
    },
    {
      key: "linked_store",
      title: t("table.column.associatedStores"),
      dataIndex: "linked_store",
      render: (_, record) => {
        const maxStoresToShow = 2;
        const stores = record.linked_store || [];
        return (
          <Flex gap={6} style={{ minWidth: 200 }}>
            {stores && (
              <>
                {stores?.length}
                <ShopOutlined /> -
              </>
            )}
            {stores.slice(0, maxStoresToShow).map((store: any, index: any) => (
              <Flex gap={5} key={index}>
                <Text>{store.name}</Text>
              </Flex>
            ))}
            {stores.length > maxStoresToShow && (
              <Flex gap={5}>
                <Tooltip
                  key={_.store_id}
                  title={stores.slice(maxStoresToShow).map((store: any) => (
                    <span key={store.id}>
                      {store.name}
                      <br />
                    </span>
                  ))}
                >
                  <Badge
                    title=""
                    count={stores.length - maxStoresToShow + "+"}
                    style={{
                      border: "1px solid",
                      borderColor: token.colorBorderSecondary,
                      background: token.colorBgBase,
                      color: token.colorTextBase,
                    }}
                  />
                </Tooltip>
              </Flex>
            )}
          </Flex>
        );
      },
    },
    {
      key: "linked_products",
      title: t("table.column.associatedProducts"),
      dataIndex: "linked_products",
      sorter: (a, b) => a.linked_products - b.linked_products,
      render: (linked_products) => {
        return (
          <Flex gap={7} style={{ minWidth: 200 }}>
            {linked_products}
          </Flex>
        );
      },
    },
    {
      key: "last_update",
      title: t("table.column.lastUpdate"),
      dataIndex: "last_update",
      sorter: (a, b) => Date.parse(a.last_update) - Date.parse(b.last_update),
      render: (last_update) => {
        return (
          <Flex gap={7} style={{ minWidth: 200 }}>
            {formatLastUpdate(last_update)}
          </Flex>
        );
      },
    },
    {
      key: "state",
      title: t("table.column.state"),
      dataIndex: "state",
      sorter: (a, b) =>
        a.state.toLowerCase().charCodeAt(0) -
        b.state.toLowerCase().charCodeAt(0),
      render: (state) => {
        return (
          <Flex gap={7}>
            <Badge status={state === "active" ? "success" : "warning"} />{" "}
            <span style={{ textTransform: "capitalize" }}>{state}</span>
          </Flex>
        );
      },
    },
  ];

  const HeaderRow = React.memo(() => (
    <>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="main_search">
            <Search
              placeholder={t("table.search.sellers")}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    main_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>
        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
        >
          <LateralModal
            buttonName={t("buttons.filters")}
            title={t("filter.title")}
          >
            <SellersFilters
              formProps={searchFormProps}
              filters={filters}
              sellersSelectProps={sellersSelectProps}
              storesSelectProps={storesSelectProps}
              t={t}
            />
          </LateralModal>

          <CreateButton
            type="primary"
            icon={<PlusOutlined />}
            iconPosition="end"
          >
            {t("buttons.newSeller")}
          </CreateButton>
        </Flex>
      </Flex>

      <FilterTags
        searchFormProps={searchFormProps}
        onSearch={onSearch}
        i18n={i18n}
        filters={filters}
        setFilters={setFilters}
        storesSelectProps={storesSelectProps}
        t={t}
      />
    </>
  ));

  const handleRowClick = (record: HttpError) => {
    push(`linked-products`);
    // El CÓDIGO, no el nombre. `smartMenuName` es lo que el SmartMenu mete en la URL de todas las
    // pantallas del seller (`/sellers/operators/:code`, `/sellers/linked-products/:code`), y la API
    // resuelve por código.
    //
    // Con el nombre funcionaba de casualidad, sólo mientras nombre y código coincidieran:
    // "Puntoseco" → `puntoseco`. Se rompe con cualquier seller cuyo nombre tenga un espacio o
    // difiera del código — "Comercial CMP" salía como `comercial%20cmp` y no existe: la grilla de
    // operadores venía vacía y la de productos linkeados tiraba error.
    //
    // La lista de Stores ya lo hacía bien (`setSmartMenuName(record.code)`); ésta se quedó atrás.
    const seller = record as unknown as { code?: string; name?: string };
    setSmartMenuName(seller.code ?? "");
    setSmartMenuLabel(seller.name ?? seller.code ?? "");
    setSmartMenuType("sellers");
    setSmartMenuState(true);
  };

  return (
    <>
      <Flex align="baseline" gap={8} style={{ marginBottom: 24 }}>
        <SellerIcon style={{ transform: "scale(1.6)", height: 14 }} />
        <Title level={3} style={{ marginBottom: 0 }}>
          {t("menu.label.sellers")}
        </Title>
      </Flex>
      <Table
        {...tableProps}
        scroll={{ x: "max-content", y: 55 * 10.5 }}
        title={() => <HeaderRow />}
        rowKey="seller_id"
        style={{
          cursor: "pointer",
        }}
        onRow={(record) => {
          return {
            onClick: () => {
              handleRowClick(record);
            },
          };
        }}
        dataSource={dataSource}
        columns={columns}
        loading={isFetching}
        locale={{
          triggerAsc: t("table.triggerAsc"),
          triggerDesc: t("table.triggerDesc"),
          cancelSort: t("table.cancelSort"),
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          locale: {
            items_per_page: t("table.itemsPerPage"),
            prev_page: t("table.prevPage"),
            next_page: t("table.nextPage"),
            jump_to: t("table.jumpTo"),
            jump_to_confirm: t("table.jumpToConfirm"),
            page: t("table.page"),
            next_5: t("table.next5"),
            prev_5: t("table.prev5"),
          },
          showTotal: (total) => (
            <PaginationTotal total={total} entityName="sellers" />
          ),
        }}
      />
      {children}
    </>
  );
};
